/**
 * Single source of truth for towns and their personas.
 * Each town has exactly one persona who is the user's "Insider".
 * Personas are Mick's IP — fixed, not customisable per business or town.
 *
 * Adding a new town = add a new entry below + register in PERSONAS map.
 * No other code should hard-code town slugs or persona names.
 *
 * Partner personas (Batch 7b-2a): when a chat session is attributed to a
 * partner with populated `partner.persona` content, the surface persona
 * is built dynamically by `buildPartnerPersona` rather than looked up in
 * PERSONAS. The two paths do NOT blend — a partner persona uses neutral
 * templates for function fields (starters, nudges, weather, memory page),
 * never falling through to the town persona's voice. Mirrors the API
 * Batch 7a architectural pivot (partner persona owns chat experience
 * when attributed; town persona is the safety net).
 */
import type { ChatMessage } from '../components/ChatBubble';
import type { Pick } from './api/picks';
import type { PublicPartner } from './api/users';
import type { WeatherSummary } from './api/weather';
import {
  dayLabel,
  extractPickAcks,
  groupHistoryByDay,
  listAnd,
  summariseDay,
  type DayActivity,
} from './memory';
import {
  capitaliseFirst,
  describeParty,
  formatStayDate,
  formatStayRange,
  type OnboardingStepId,
  type Profile,
} from './onboarding';
export interface PersonaOnboarding {
  /** Persona's intro line, shown before the first question. */
  readonly welcomeIntro: string;
  /** Per-step prompts in this persona's voice. */
  readonly prompts: Readonly<Record<OnboardingStepId, string>>;
  /** Persona's confirmation message after the profile is captured. */
  readonly buildAck: (profile: Profile) => string;
  /**
   * Capability-signalling welcome message fired AFTER `buildAck` once
   * onboarding completes. Sets expectations for what the persona can
   * help with (tides, weather, ferry, where to eat, etc.) and signals
   * always-available support — replacing the earlier design where the
   * 4pm teatime nudge fired immediately as a de facto welcome on
   * post-16:00 sign-ups. The teatime nudge is now gated to skip the
   * sign-up day, so it can't double up with this welcome.
   *
   * Persona-voiced and capability-aware: phrasing matches the persona's
   * register (lyrical-spare for Morwenna, direct-weatherly for Jago,
   * storyteller-slow for Demelza), and the named capabilities match
   * what the persona can actually help with via the API's tool surface.
   */
  readonly welcomeAfterOnboarding: string;
}
export interface PersonaTeatimeNudge {
  /** Compose the full tea-time message in this persona's voice. */
  readonly format: (picks: ReadonlyArray<Pick>) => string;
  /** Persona-voiced fallback if the picks fetch fails. */
  readonly errorReply: string;
}
export interface PersonaNudges {
  readonly teatime: PersonaTeatimeNudge;
}
export interface PersonaWeather {
  /** Formats a WeatherSummary into a single persona-voiced sentence/two. */
  readonly format: (summary: WeatherSummary) => string;
  /** Persona-voiced fallback if the weather fetch fails. */
  readonly errorReply: string;
}
export interface PersonaCoords {
  readonly lat: number;
  readonly lng: number;
}
/**
 * Suggestion chip shown above ChatInput post-onboarding. Tapping the chip
 * sends `prompt` as the user message; `label` is what's rendered on the chip.
 * Label and prompt may differ (compact label, fuller prompt).
 */
export interface PersonaStarter {
  readonly label: string;
  readonly prompt: string;
}
/**
 * Structured content for the user's Memory Page — a persona-voiced
 * keepsake of their stay. Rendered by `pages/MemoryPage.tsx`.
 *
 * Composition is driven by both the user's profile and (when available)
 * their chat history. Profile drives the existing 3-paragraph narrative
 * — title, subtitle, persona-voiced reflection. History drives the
 * enrichment fields (`daysVisited`, `placesAcknowledged`) — populated
 * when MemoryPage passes history into composeMemoryPage; empty arrays
 * otherwise so the page renders cleanly cold-start (e.g. just-onboarded
 * user opening their Memory Page on day 1).
 *
 * Why structured rather than a single string: it lets the page render
 * heading / body / phrase / closing / timeline / places list with
 * distinct typography without the persona function caring about
 * presentation. The persona owns the voice; the page owns the layout.
 */
export interface MemoryPageContent {
  /** e.g. "A Padstow week" — personalised with the user's name when
   *  set (v7), e.g. "Sarah's Padstow week". Partner-attributed
   *  keepsakes are partner-led: e.g. "Sarah's Harlyn Sands Holiday
   *  Park stay". */
  readonly title: string;
  /** e.g. "7–14 May · with Morwenna" — the date range, em-dash separated
   *  from the persona attribution. */
  readonly subtitle: string;
  /** 2–3 narrative paragraphs in persona voice. The first paragraph is
   *  expected to interpolate the profile (party, dog, dates, name when
   *  present); the others carry persona-flavoured colour. */
  readonly paragraphs: ReadonlyArray<string>;
  /** Day-by-day timeline. Empty when no history was supplied to
   *  composeMemoryPage (cold-start render). Sorted chronologically. */
  readonly daysVisited: ReadonlyArray<{
    readonly dateLocal: string;
    readonly label: string;
    /** Persona-voiced one-liner summarising the day. */
    readonly summary: string;
  }>;
  /** Venues the user picked across the week, deduplicated. Empty when
   *  no pick-ack messages in history (or no history at all). Sorted by
   *  first-pick date. */
  readonly placesAcknowledged: ReadonlyArray<{
    readonly name: string;
    readonly dateLocal: string;
    /** Display label, e.g. "Wednesday" or "7 May" depending on
     *  whether the date sits in the user's stay window. */
    readonly dateLabel: string;
  }>;
  /** Cornish phrase from the persona, presented as a small block at the
   *  end of the page. Town personas (Morwenna / Jago / Demelza) supply
   *  one — their own greeting from the welcome intro, repeated here
   *  for voice continuity. Partner personas omit this entirely (Batch
   *  8a): the field is optional and `MemoryPage` gates the rendered
   *  block on its presence, so a Harlyn Sands keepsake doesn't carry a
   *  Cornish flourish that isn't the partner's. */
  readonly cornishPhrase?: {
    readonly word: string;
    readonly meaning: string;
  };
  /** Single italicised closing line, set apart from the body — like a
   *  signing-off remark or benediction. */
  readonly closingLine: string;
}
export interface Persona {
  readonly slug: string;
  readonly townLabel: string;
  readonly personaName: string;
  /** Internal voice direction — not rendered, kept here so future copy stays in character. */
  readonly voiceNote: string;
  /** Geographic centre of the town — used for weather, distance calcs, etc. */
  readonly coords: PersonaCoords;
  readonly onboarding: PersonaOnboarding;
  /** Persona-voiced nudges (4pm push, future wet-day, last-evening, etc.) */
  readonly nudges: PersonaNudges;
  /** Persona-voiced weather formatter. */
  readonly weather: PersonaWeather;
  /**
   * Suggestion chips shown above ChatInput post-onboarding. Tapping a
   * chip sends `prompt` as the user message; `label` is the chip text.
   *
   * Locked uniform across every persona (town and partner) as of
   * 13 Jun 2026 — see DEFAULT_STARTERS below for the rationale. The
   * `profile` parameter is retained in the signature for future per-
   * profile customisation but is currently ignored by every persona.
   */
  readonly getStarters: (profile: Profile) => ReadonlyArray<PersonaStarter>;
  /**
   * Persona-voiced acknowledgment when the user names a venue from a
   * recently-shown picks list. v1: simple acknowledgment + invitation to
   * report back. Real booking flow lands with the API repo and a business
   * agent ("Tom") taking over from this hand-off point.
   */
  readonly acknowledgePick: (pick: Pick) => string;
  /**
   * Persona-voiced ack appended to chat history when the user updates
   * their profile via the settings page. Same `buildAck` shape as the
   * onboarding ack so the format reads as a natural continuation rather
   * than a different style of system notice.
   */
  readonly profileUpdatedAck: (profile: Profile) => string;
  /**
   * Compose the user's Memory Page content in this persona's voice.
   * `history` is optional — when provided, the persona enriches the
   * content with day-by-day timeline and "places you went" sections.
   * Cold-start callers (no history yet) get the profile-driven base
   * content with empty enrichment arrays.
   */
  readonly composeMemoryPage: (
    profile: Profile,
    history?: ReadonlyArray<ChatMessage>,
  ) => MemoryPageContent;
  /**
   * Persona-voiced one-liner offered near the end of the user's stay,
   * pointing them at their Memory Page. Fired once per stay by the
   * trigger in MePage (today === stayEnd or stayEnd - 1). References
   * the always-visible book icon in the MePage header so the user
   * knows where to find the page on any future visit too — pairing a
   * one-time spoken cue with an ongoing visual affordance.
   */
  readonly memoryPageSuggestion: string;
  /**
   * Persona-voiced fallback used when a chat send fails.
   * Surfaced as a normal persona bubble so the user knows to retry.
   */
  readonly errorReply: string;
}
/**
 * Shared ack body. Each persona supplies its own opener and (optionally)
 * a closer to keep voice distinct without duplicating the structural
 * sentence each time.
 *
 * Format with name + closer: "{opener-without-period}, {name}. {dateRange} — {party}, {dog}. {closer}"
 *   e.g. "Right, Sarah. 7–14 May — just you, no dog. Sorted."
 *
 * Format with name, no closer: "{opener-without-period}, {name}. {dateRange} — {party}, {dog}."
 *   e.g. "Lovely, Sarah. 7–14 May — just you, no dog."
 *
 * Format without name + closer: "{opener} {dateRange} — {party}, {dog}. {closer}"
 *   e.g. "Right. 7–14 May — just you, no dog. Sorted."
 *
 * Format without name, no closer: "{opener} {dateRange} — {party}, {dog}."
 *   e.g. "Lovely. 7–14 May — just you, no dog."
 *
 * Name folding: the opener's trailing period is stripped, the name is
 * appended with a comma, then the period re-added — a single warm beat
 * without forcing every persona's opener to be name-aware. Empty closer
 * is omitted entirely (no trailing space). Same shape used for the
 * onboarding ack and the profile-update ack so voice stays consistent
 * across both entry points.
 *
 * Allergies dropped 3 Jun 2026 — descope. The persona handles dietary
 * requirements via the venue phone handoff (see Morwenna's BOOKINGS
 * section in towninsider-api/src/lib/personas/morwenna.ts).
 */
function buildAck(opener: string, closer: string, profile: Profile): string {
  const dateRange = formatStayRange(profile.stayStart, profile.stayEnd);
  const party = describeParty(profile.party);
  const dog = profile.hasDog ? 'with a dog' : 'no dog';
  const tail = closer ? ` ${closer}` : '';
  const greeting = profile.name
    ? `${opener.replace(/\.$/, '')}, ${profile.name}.`
    : opener;
  return `${greeting} ${dateRange} \u2014 ${party}, ${dog}.${tail}`;
}
/**
 * Shared opening sentence for every TOWN-led Memory Page. Sets the scene
 * with the dates + profile facts before the persona's voice takes over.
 *
 * Format without name: "{greeting}. You were here in {town}..."
 * Format with name:    "{greeting}, {name}. You were here in {town}..."
 *
 * The persona supplies the greeting (Cornish word from their intro) and
 * the trailing continuation; this helper handles the structural middle.
 *
 * Allergies dropped 3 Jun 2026 (descope).
 */
function buildMemoryOpener(
  greeting: string,
  town: string,
  profile: Profile,
  continuation: string,
): string {
  const start = formatStayDate(profile.stayStart);
  const end = formatStayDate(profile.stayEnd);
  const party = describeParty(profile.party);
  const dog = profile.hasDog ? ', with a dog' : '';
  const opener = profile.name ? `${greeting}, ${profile.name}` : greeting;
  return `${opener}. You were here in ${town} from ${start} to ${end} \u2014 ${party}${dog}. ${continuation}`;
}
/**
 * Memory page title — possessive form when a name is set, generic
 * otherwise. "A Padstow week" → "Sarah's Padstow week".
 */
function memoryTitle(townLabel: string, profile: Profile): string {
  return profile.name
    ? `${profile.name}'s ${townLabel} week`
    : `A ${townLabel} week`;
}
/**
 * Partner-led memory page title — used by `buildPartnerPersona`'s
 * `composeMemoryPage`. The keepsake's headline is the partner business
 * (the artefact the guest will save or print should foreground the
 * paying partner's brand); the town is named in the body opener.
 *
 * With name: "Sarah's Harlyn Sands Holiday Park stay"
 * Without:   "A stay at Harlyn Sands Holiday Park"
 */
function memoryTitlePartner(partnerName: string, profile: Profile): string {
  return profile.name
    ? `${profile.name}'s ${partnerName} stay`
    : `A stay at ${partnerName}`;
}
/**
 * Partner-led opening sentence for the Memory Page. Parallel structure
 * to `buildMemoryOpener` but anchors on the partner accommodation rather
 * than the town — "You stayed with us at {partner}" rather than "You
 * were here in {town}". The town can still be mentioned by name in
 * later paragraphs as the surrounding area.
 *
 * With name: "Hi, Sarah. You stayed with us at Harlyn Sands Holiday Park from 1 Jun to 8 Jun — a family, with a dog. {continuation}"
 * Without:   "Hi. You stayed with us at Harlyn Sands Holiday Park from 1 Jun to 8 Jun — a family. {continuation}"
 */
function buildPartnerMemoryOpener(
  partnerName: string,
  profile: Profile,
  continuation: string,
): string {
  const start = formatStayDate(profile.stayStart);
  const end = formatStayDate(profile.stayEnd);
  const party = describeParty(profile.party);
  const dog = profile.hasDog ? ', with a dog' : '';
  const opener = profile.name ? `Hi, ${profile.name}` : 'Hi';
  return `${opener}. You stayed with us at ${partnerName} from ${start} to ${end} \u2014 ${party}${dog}. ${continuation}`;
}
/**
 * Take a profile-driven base content and (when history is supplied)
 * enrich it with day-by-day timeline and "places you went" entries
 * composed via the persona's daySummary helper. Returns the base
 * unchanged if no history or no extractable history days.
 *
 * Layered design: extraction primitives in memory.ts, voice composition
 * via the daySummary callback (one per persona), orchestration here.
 * That keeps each concern in one place — adding a fourth persona only
 * needs a daySummary helper, not new orchestration code.
 */
function enrichMemoryPage(
  base: MemoryPageContent,
  profile: Profile,
  history: ReadonlyArray<ChatMessage> | undefined,
  daySummary: (activity: DayActivity) => string,
): MemoryPageContent {
  if (!history || history.length === 0) return base;
  const days = groupHistoryByDay(history, profile);
  if (days.length === 0) return base;
  const daysVisited = days.map((d) => ({
    dateLocal: d.dateLocal,
    label: d.label,
    summary: daySummary(summariseDay(history, d.dateLocal)),
  }));
  const placesAcknowledged = extractPickAcks(history).map((p) => ({
    name: p.name,
    dateLocal: p.firstDateLocal,
    dateLabel: dayLabel(p.firstDateLocal, profile),
  }));
  return { ...base, daysVisited, placesAcknowledged };
}
// ───────────────────────────────────────────────────────────────────────
// Per-persona daySummary helpers — voice composers for the timeline.
// Each takes a voice-free DayActivity and returns a one-liner in the
// persona's distinct register. Kept module-private; exposed via the
// persona's composeMemoryPage rather than as standalone exports.
//
// Branch order (Batch 13, refined 14):
//   1. venuesPicked — most concrete thing the user did that day.
//   2. Topics-list — collects any flags that fired (weather / tides /
//      food / walks / beach), renders as "{persona-verb} {oxford-
//      comma list of topic nouns}". Each persona uses its own verb
//      ("We talked through" / "Talked" / "We considered" / "We
//      covered") and topic-noun phrasings ("the sky" vs "the wind"
//      vs "the weather" for the same weather flag) to preserve voice.
//   3. updatedProfile.
//   4. "we chatted" fallback. A day only reaches daySummary when there
//      were messages that day (groupHistoryByDay's pre-filter), so the
//      fallback acknowledges a conversation happened rather than
//      claiming the day was "silent" — which would always be wrong.
//
// Topic-noun naming (Batch 14): the FOOD flag uses "where to eat" /
// "food" / "what to eat" rather than "tea" — the keyword pattern
// captures brunch/cafe/coffee/etc, and "tea" (strictly the British
// evening meal) was misleading as a surface noun when those broader
// queries fired. The WALKS flag uses "a walk" (not "a walk with the
// dog") — the previous DOG-named flag matched onboarding selections
// like "Couple + dog" and produced false positives, so the flag is
// now keyed on movement vocabulary instead and the topic noun is
// dog-agnostic.
// ───────────────────────────────────────────────────────────────────────
function morwennaDaySummary(activity: DayActivity): string {
  if (activity.venuesPicked.length > 0) {
    return `You found ${listAnd(activity.venuesPicked)}.`;
  }
  const topics: string[] = [];
  if (activity.askedAboutWeather) topics.push('the sky');
  if (activity.askedAboutTides) topics.push('the tide');
  if (activity.askedAboutFood) topics.push('where to eat');
  if (activity.askedAboutWalks) topics.push('a walk');
  if (activity.askedAboutBeach) topics.push('the beach');
  if (topics.length > 0) return `We talked through ${listAnd(topics)}.`;
  if (activity.updatedProfile) {
    return 'You set me straight on a few details.';
  }
  return 'A quiet one \u2014 we chatted.';
}
function jagoDaySummary(activity: DayActivity): string {
  const n = activity.venuesPicked.length;
  if (n === 1) return `You went to ${listAnd(activity.venuesPicked)}.`;
  if (n > 1) return `${listAnd(activity.venuesPicked)} \u2014 proper day, that.`;
  const topics: string[] = [];
  if (activity.askedAboutWeather) topics.push('the wind');
  if (activity.askedAboutTides) topics.push('the tide');
  if (activity.askedAboutFood) topics.push('food');
  if (activity.askedAboutWalks) topics.push('a walk');
  if (activity.askedAboutBeach) topics.push('the beach');
  if (topics.length > 0) return `Talked ${listAnd(topics)}.`;
  if (activity.updatedProfile) return 'Updated your details.';
  return 'Quiet one. Just words.';
}
function demelzaDaySummary(activity: DayActivity): string {
  const n = activity.venuesPicked.length;
  if (n === 1) return `You settled on ${listAnd(activity.venuesPicked)}.`;
  if (n > 1) return `${listAnd(activity.venuesPicked)} \u2014 what a day.`;
  const topics: string[] = [];
  if (activity.askedAboutWeather) topics.push('the sky');
  if (activity.askedAboutTides) topics.push('the river');
  if (activity.askedAboutFood) topics.push('where to eat');
  if (activity.askedAboutWalks) topics.push('a walk');
  if (activity.askedAboutBeach) topics.push('the beach');
  if (topics.length > 0) return `We considered ${listAnd(topics)}.`;
  if (activity.updatedProfile) return 'You let me know a few details.';
  return 'A still one \u2014 just a chat.';
}
/**
 * Neutral daySummary used by partner personas. Avoids town-specific
 * imagery (no estuary, no harbour, no "sky over Padstow") and avoids
 * any town-persona voice tics — partner personas have their own voice
 * upstream in chat (driven by partner.persona's tone register and
 * voice guidelines on the API side); this client-side helper is a
 * neutral structural fallback for the memory page timeline.
 *
 * Topics list (Batch 13/14): "We covered the tides, what to eat, and
 * a walk." replaces the previous generic "We had a chat about your
 * stay" fallback as soon as any user-typed message matches a topic
 * keyword in summariseDay. The "had a chat" line now only fires when
 * the user sent messages but typed nothing topic-matching — rare in
 * real use.
 */
function partnerDaySummary(activity: DayActivity): string {
  const n = activity.venuesPicked.length;
  if (n === 1) return `You went to ${listAnd(activity.venuesPicked)}.`;
  if (n > 1) return `${listAnd(activity.venuesPicked)} \u2014 a good day.`;
  const topics: string[] = [];
  if (activity.askedAboutWeather) topics.push('the weather');
  if (activity.askedAboutTides) topics.push('the tides');
  if (activity.askedAboutFood) topics.push('what to eat');
  if (activity.askedAboutWalks) topics.push('a walk');
  if (activity.askedAboutBeach) topics.push('the beach');
  if (topics.length > 0) return `We covered ${listAnd(topics)}.`;
  if (activity.updatedProfile) return 'You updated your details.';
  return 'We had a chat about your stay.';
}
/** Helper: shape three lines into the standard tea-time message body. */
function composeTeatimeMessage(
  intro: string,
  picks: ReadonlyArray<Pick>,
  closer: string,
): string {
  const lines = [intro, ...picks.map((p) => `\u2022 ${p.name} \u2014 ${p.blurb}`), closer];
  return lines.join('\n');
}
/**
 * Default starter chips — shared across every persona (town and
 * partner) as of 13 Jun 2026.
 *
 * Two compact chips covering the highest-intent Cornwall queries
 * (weather and tides). Locked uniform after the previous per-persona
 * / per-profile branching produced 3-row chip stacks that ate too
 * much chat-input real estate on narrow mobile widths.
 *
 * If TownInsider ever launches in non-coastal towns the tide chip
 * will need to become persona-conditional — every Cornwall persona
 * honours it uniformly for now (Padstow / Newquay / Fowey are all
 * coastal, and so are the partner accommodations attributed to them).
 *
 * Object.freeze locks the array shape at runtime alongside the
 * `readonly` modifiers on PersonaStarter — the constant is the
 * single source of truth and must not be mutated by accident.
 */
const DEFAULT_STARTERS: ReadonlyArray<PersonaStarter> = Object.freeze([
  Object.freeze({ label: 'Weather', prompt: "What's the weather today?" }),
  Object.freeze({ label: 'Tide times', prompt: 'What are the tide times today?' }),
]);
const PADSTOW: Persona = {
  slug: 'padstow',
  townLabel: 'Padstow',
  personaName: 'Morwenna',
  voiceNote:
    'Warm, lyrical, hand-touched. Harbour-and-moor imagery. Serif prose-local for her bubbles.',
  coords: { lat: 50.5413, lng: -4.9357 },
  onboarding: {
    welcomeIntro:
      "Dohajydh da. I'm Morwenna, your Padstow Insider. A few quick things and I'll have your week set up properly.",
    prompts: {
      name: 'And what shall I call you?',
      'stay-dates':
        'When are you here? Pick your arrival and the day you head off.',
      'party-with-dog': "Who's joining you \u2014 and is there a dog with you?",
    },
    buildAck: (p) => buildAck('Lovely.', '', p),
    welcomeAfterOnboarding:
      "That's all set up now. I'm here for the whole of your holiday \u2014 anything you want to know, from ferries to weather to tides to where to eat, just ask. Day or night, I'm here for you.",
  },
  nudges: {
    teatime: {
      format: (picks) =>
        composeTeatimeMessage(
          "Right \u2014 what's for tea? Three for tonight:",
          picks,
          "Tell me which way you're leaning and I'll handle it.",
        ),
      errorReply:
        'I had something lined up for tea but lost the thread \u2014 give me a moment and ask me again.',
    },
  },
  weather: {
    format: (s) =>
      `Sky's ${s.conditionsPhrase} over Padstow \u2014 ${s.tempC}\u00b0C, ${s.windPhrase}. ${capitaliseFirst(s.forwardNote)}.`,
    errorReply:
      "Can't see the sky just now \u2014 give me a moment and ask me again.",
  },
  getStarters: () => DEFAULT_STARTERS,
  acknowledgePick: (pick) =>
    `${pick.name} \u2014 good shout. Come back and tell me how it was.`,
  profileUpdatedAck: (p) =>
    buildAck('Updated.', "I'll keep that in mind for the rest of the week.", p),
  composeMemoryPage: (profile, history) => {
    const base: MemoryPageContent = {
      title: memoryTitle('Padstow', profile),
      subtitle: `${formatStayRange(profile.stayStart, profile.stayEnd)} \u00b7 with Morwenna`,
      paragraphs: [
        buildMemoryOpener(
          'Dohajydh da',
          'Padstow',
          profile,
          "Here's how I remember it.",
        ),
        "I kept an eye on the tide for you, watched the sky, and pointed at the kitchens worth your time. Three picks a day, the right ones. You took some, you didn't take others \u2014 that's the way of it.",
        'You came to Padstow knowing the harbour by name. You leave knowing which corner of it the locals stand at when the boats come in. That is the difference a few days here makes.',
      ],
      daysVisited: [],
      placesAcknowledged: [],
      cornishPhrase: {
        word: 'Dohajydh da',
        meaning: '"good afternoon" in Kernewek',
      },
      closingLine: 'The harbour will keep. Come back.',
    };
    return enrichMemoryPage(base, profile, history, morwennaDaySummary);
  },
  memoryPageSuggestion:
    "Your week's quietly waiting. Tap the book up top whenever you'd like to read it back.",
  errorReply:
    'Lost the thread there for a moment \u2014 would you mind sending that again?',
};
const NEWQUAY: Persona = {
  slug: 'newquay',
  townLabel: 'Newquay',
  personaName: 'Jago',
  voiceNote:
    'Salty skipper. Direct, tide-and-wind imagery, less ornate than Morwenna. Practical, weatherly, knowing.',
  coords: { lat: 50.4143, lng: -5.076 },
  onboarding: {
    welcomeIntro:
      "Lowena dhis. I'm Jago, your Newquay Insider. Quick few and I'm out of your way.",
    prompts: {
      name: "First off \u2014 what's the name?",
      'stay-dates': 'When are you down? Pick the days you\u2019re around.',
      'party-with-dog': "Who's with you \u2014 and is there a dog?",
    },
    buildAck: (p) => buildAck('Right.', 'Sorted.', p),
    welcomeAfterOnboarding:
      "Right, that's you set up. I'm here for the whole of your week \u2014 tide times, wind, where to eat, what's open, anything you want to know. Just holler. Day or night, doesn't matter to me.",
  },
  nudges: {
    teatime: {
      format: (picks) =>
        composeTeatimeMessage(
          "Tide's turning \u2014 tea time. Three options:",
          picks,
          "Pick one, I'll sort it.",
        ),
      errorReply:
        'Had picks lined up for tea but the line dropped \u2014 try me again in a sec.',
    },
  },
  weather: {
    format: (s) =>
      `Newquay just now: ${s.tempC}\u00b0C, ${s.conditionsPhrase}, ${s.windPhrase}. ${capitaliseFirst(s.forwardNote)}.`,
    errorReply: 'Lost the signal on the forecast \u2014 try again in a sec.',
  },
  getStarters: () => DEFAULT_STARTERS,
  acknowledgePick: (pick) =>
    `${pick.name}, then. Solid pick. Holler if anything goes sideways.`,
  profileUpdatedAck: (p) => buildAck('Right, updated.', 'Sorted.', p),
  composeMemoryPage: (profile, history) => {
    const base: MemoryPageContent = {
      title: memoryTitle('Newquay', profile),
      subtitle: `${formatStayRange(profile.stayStart, profile.stayEnd)} \u00b7 with Jago`,
      paragraphs: [
        buildMemoryOpener(
          'Lowena dhis',
          'Newquay',
          profile,
          'The tide ran in and out a few times while you were here.',
        ),
        "I gave you the picks each day \u2014 the quiet ones if you wanted them, the busy ones if you didn't. The wind did what wind does. The cliffs are still where you left them.",
        "Best thing I can tell you: come back. The good corners aren't on the postcards.",
      ],
      daysVisited: [],
      placesAcknowledged: [],
      cornishPhrase: {
        word: 'Lowena dhis',
        meaning: '"joy to you" in Kernewek',
      },
      closingLine: 'Mind the tide on the way out.',
    };
    return enrichMemoryPage(base, profile, history, jagoDaySummary);
  },
  memoryPageSuggestion:
    'Set your week down for you. Tap the book up top before you head off.',
  errorReply: 'Signal dropped on me \u2014 give that another go?',
};
const FOWEY: Persona = {
  slug: 'fowey',
  townLabel: 'Fowey',
  personaName: 'Demelza',
  voiceNote:
    "Quiet, attentive, heartfelt-maker. Storyteller's cadence. River-mouth slowness \u2014 du Maurier country.",
  coords: { lat: 50.3358, lng: -4.636 },
  onboarding: {
    welcomeIntro:
      "Gorthugher da. I'm Demelza, your Fowey Insider. A few quick things, then I'll let you settle in.",
    prompts: {
      name: 'What shall I call you?',
      'stay-dates': 'When are you with us? Choose the days you\u2019re here.',
      'party-with-dog': "Who's keeping you company, and any dog along?",
    },
    buildAck: (p) => buildAck('Good to know.', "I've got you.", p),
    welcomeAfterOnboarding:
      "There. That's all set. I'll be with you for the whole of your week \u2014 anything you want to know, the river, the weather, what's on, where to settle for dinner, a corner to walk to. Just say, any time at all.",
  },
  nudges: {
    teatime: {
      format: (picks) =>
        composeTeatimeMessage(
          "The day's softening \u2014 what shall we do for tea? Three thoughts:",
          picks,
          'Whichever feels right, just say.',
        ),
      errorReply:
        'I had something thought through for tea, but it slipped just now. Give me a moment and ask me again.',
    },
  },
  weather: {
    format: (s) =>
      `Fowey at the moment \u2014 ${s.tempC}\u00b0C, ${s.conditionsPhrase}, ${s.windPhrase}. ${capitaliseFirst(s.forwardNote)}.`,
    errorReply:
      "The sky's gone shy on me \u2014 give me a moment and ask again.",
  },
  getStarters: () => DEFAULT_STARTERS,
  acknowledgePick: (pick) =>
    `${pick.name} \u2014 quietly perfect. Let me know what you make of it.`,
  profileUpdatedAck: (p) =>
    buildAck('Got it.', "I'll hold that in mind for the rest of the week.", p),
  composeMemoryPage: (profile, history) => {
    const base: MemoryPageContent = {
      title: memoryTitle('Fowey', profile),
      subtitle: `${formatStayRange(profile.stayStart, profile.stayEnd)} \u00b7 with Demelza`,
      paragraphs: [
        buildMemoryOpener(
          'Gorthugher da',
          'Fowey',
          profile,
          'The river was kind, and the days softened in their proper order.',
        ),
        "I told you where to eat and where the locals don't go. You found a few corners on your own, I'd guess. That's how it should be.",
        "There's a quietness to this place that takes a few days to settle into you. I hope it has.",
      ],
      daysVisited: [],
      placesAcknowledged: [],
      cornishPhrase: {
        word: 'Gorthugher da',
        meaning: '"good evening" in Kernewek',
      },
      closingLine: 'The river will be here when you come back.',
    };
    return enrichMemoryPage(base, profile, history, demelzaDaySummary);
  },
  memoryPageSuggestion:
    "I've kept the shape of your week. The book up top will open it whenever you'd like a look.",
  errorReply: 'Something snagged just then \u2014 would you mind trying again?',
};
const PERSONAS: Readonly<Record<string, Persona>> = Object.freeze({
  padstow: PADSTOW,
  newquay: NEWQUAY,
  fowey: FOWEY,
});
export const KNOWN_TOWN_SLUGS: ReadonlyArray<string> = Object.freeze(
  Object.keys(PERSONAS),
);
/**
 * Town slugs that are LIVE for the current launch — the subset of
 * KNOWN_TOWN_SLUGS whose catalogue is seeded in the production API.
 *
 * A town graduates from "known" (a persona is defined above) to
 * "launch" (catalogue seeded, the API will accept and serve it) only
 * when its seed data ships. Soft launch is Padstow alone — Newquay and
 * Fowey have personas defined above but no seeded catalogue, so they
 * must not be offered as onboardable towns yet. Keep this in sync with
 * the seeded towns in the `towninsider-api` repo (`seeds/`).
 */
export const LAUNCH_TOWN_SLUGS: ReadonlyArray<string> = Object.freeze([
  'padstow',
]);
/** True if the given string is a recognised town slug (lowercase). */
export function isKnownTownSlug(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(PERSONAS, slug);
}
/**
 * Look up a persona by town identifier.
 * Accepts either canonical slug ("padstow") or capitalised label ("Padstow") —
 * the latter handles legacy localStorage entries from before the slug-canonical
 * storage refactor.
 *
 * Returns null for unknown towns. Callers MUST handle null — it indicates
 * either a stale entry or a tampered storage value, and the user should
 * be bounced rather than shown a half-rendered persona.
 */
export function getPersona(slugOrLabel: string): Persona | null {
  const normalised = slugOrLabel.toLowerCase();
  return PERSONAS[normalised] ?? null;
}
// ───────────────────────────────────────────────────────────────────────
// Partner personas — Batch 7b-2a
//
// When a chat session is attributed to a partner that has populated
// `partner.persona` content (PublicPartner.persona on the wire), the
// surface persona is built dynamically by `buildPartnerPersona` rather
// than looked up in PERSONAS. The two paths do NOT blend:
//   - Partner persona uses partner-curated identity (name, greeting,
//     welcomeAfterOnboarding) AND neutral templates for the operational
//     surface (starters, nudges, weather formatter, memory page).
//   - Town persona's voice (Morwenna's Kernewek phrases, Padstow imagery,
//     etc.) does NOT leak into the partner persona's surface — that's
//     the "no blending" principle, mirroring the API Batch 7a
//     architectural pivot.
//
// `resolvePersona` is the single entry point — given a town and a
// (maybe-null) partner, returns the effective persona for chat. Use
// this in MePage / JoinPage / any future consumer so the branching
// lives in one place.
// ───────────────────────────────────────────────────────────────────────
/**
 * Slugify a partner's business name into a stable url-safe token.
 * Lowercased, non-alphanumerics collapsed to single hyphens, leading /
 * trailing hyphens trimmed. Used to compose the partner persona's slug
 * (analytics-friendly: `${townSlug}-partner-${nameSlug}`).
 *
 * Idempotent — re-slugifying a slug yields the same slug. Defensive
 * against partner names with quotes, parentheses, ampersands, etc.
 */
function slugifyPartnerName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
/**
 * Build a partner-attributed Persona — the app-side counterpart to
 * `buildPartnerPersonaPrompt` on the API (Batch 7a).
 *
 * Architecture (Batch 7b-2a, matches API Batch 7a):
 *   When a user is attributed to a partner whose `persona` projection
 *   is non-null (PublicPartnerPersona, sourced from /redeem and /me),
 *   the app surfaces the partner's identity AND composes a full
 *   `Persona` object so the rest of the app code (MePage, MemoryPage,
 *   starters, nudges, weather formatter) treats partner and town
 *   personas interchangeably.
 *
 * What's CURATED by the partner — sourced from PublicPartnerPersona:
 *   - `personaName` ← `partnerPersona.name`
 *   - `onboarding.welcomeIntro` ← `partnerPersona.greeting`
 *   - `onboarding.welcomeAfterOnboarding` ← `partnerPersona.welcomeAfterOnboarding`
 *
 * What's BORROWED from the town context — town FACTS, not town voice:
 *   - `townLabel`
 *   - `coords`
 *
 * What's NEUTRAL — generic templates so the town persona's voice
 * (Morwenna's Kernewek phrasings, Padstow-specific imagery, harbour
 * gulls etc.) does NOT leak into the partner persona's surface. A
 * partner persona is its own identity; this is the "no blending"
 * principle (API Batch 7a). Onboarding prompts, teatime nudge format,
 * weather formatter, starters all use neutral, town-name-templated text.
 *
 * Starters use DEFAULT_STARTERS (locked uniform across all personas
 * 13 Jun 2026) — same two compact chips that every town persona
 * exposes, no per-profile branching. Premium UX: chips fit on one
 * line on narrow mobile widths and the experience stays consistent
 * across town and partner attribution.
 *
 * Memory page (Batch 8a): title is partner-led ("Sarah's Harlyn Sands
 * Holiday Park stay"), subtitle carries persona attribution only
 * ("1–8 Jun · with Ollie"), body opens at the partner accommodation
 * and names the town in supporting context. The `cornishPhrase` slot
 * is omitted entirely — the field is optional on MemoryPageContent and
 * the page UI gates the rendered block on its presence. Per-partner
 * bespoke memory page copy is a deferred follow-up (Batch 8b); until
 * then this neutral-but-partner-named baseline is the keepsake.
 *
 * `slug` is a unique composite (`${townSlug}-partner-${partnerNameSlug}`)
 * so analytics can distinguish partner-attributed chats from town-default
 * chats on the same town. Internal identifier; never rendered.
 *
 * Returns null when the town slug is not recognised — same posture as
 * `getPersona` for un-launched / unknown towns. Caller must handle.
 *
 * Throws when `partner.persona` is null — caller must check before
 * calling. Use `resolvePersona` for the safe branching wrapper.
 */
export function buildPartnerPersona(
  partner: PublicPartner,
  townSlug: string,
): Persona | null {
  if (!partner.persona) {
    throw new Error(
      'buildPartnerPersona called with null partner.persona \u2014 caller must check partner.persona is non-null, or use resolvePersona for the safe branching wrapper',
    );
  }
  const townPersona = PERSONAS[townSlug.toLowerCase()];
  if (!townPersona) return null;
  const partnerPersona = partner.persona;
  const personaName = partnerPersona.name;
  const partnerBusinessName = partner.name;
  const townLabel = townPersona.townLabel;
  const composedSlug = `${townPersona.slug}-partner-${slugifyPartnerName(partner.slug)}`;
  return {
    slug: composedSlug,
    townLabel,
    personaName,
    voiceNote:
      'Partner-curated voice. Tone register and voice guidelines live in partner.persona on the API side and feed buildPartnerPersonaPrompt \u2014 never rendered on the client.',
    coords: townPersona.coords,
    onboarding: {
      // Partner-curated greeting opens the onboarding flow. Sets
      // partner identity from the first beat so the post-onboarding
      // welcome chip doesn't feel like an identity switch.
      welcomeIntro: partnerPersona.greeting,
      // Neutral partner-default prompts \u2014 no town-specific imagery,
      // no Kernewek phrases, AND deliberately distinct from any town
      // persona's prompts. The "no blending" principle (API Batch 7a)
      // means partner identity must not echo town-persona wording even
      // for purely structural questions: a Harlyn guest seeing the
      // literal same prompt as a Padstow town-persona guest erodes the
      // sense that the partner persona is its own identity. Accommodation
      // vocabulary ("check out") replaces Morwenna's folksy "head off"
      // so the defaults read as warm-but-stay-neutral and slot under any
      // commercial accommodation partner without curation. Enforced by
      // src/lib/personas.test.ts ("no town voice leaking"): if these
      // strings drift back toward any town persona's, the test fails.
      prompts: {
        name: "First things first \u2014 what's your name?",
        'stay-dates':
          'When are you staying? Pick your arrival day and the day you check out.',
        'party-with-dog': "Who's joining you \u2014 and is there a dog?",
      },
      buildAck: (p) => buildAck('All set.', '', p),
      welcomeAfterOnboarding: partnerPersona.welcomeAfterOnboarding,
    },
    nudges: {
      teatime: {
        format: (picks) =>
          composeTeatimeMessage(
            'Tea time \u2014 three options for tonight:',
            picks,
            'Tell me which you fancy and I\u2019ll point you at the phone.',
          ),
        errorReply:
          'Lost my line on tea picks \u2014 give it a moment and ask me again.',
      },
    },
    weather: {
      format: (s) =>
        `${townLabel}: ${s.tempC}\u00b0C, ${s.conditionsPhrase}, ${s.windPhrase}. ${capitaliseFirst(s.forwardNote)}.`,
      errorReply:
        'Can\u2019t see the sky just now \u2014 give me a moment and ask again.',
    },
    getStarters: () => DEFAULT_STARTERS,
    acknowledgePick: (pick) =>
      `${pick.name} \u2014 good shout. Come back and tell me how it was.`,
    profileUpdatedAck: (p) =>
      buildAck(
        'Updated.',
        'I\u2019ll keep that in mind for the rest of your stay.',
        p,
      ),
    composeMemoryPage: (profile, history) => {
      // Partner-led keepsake (Batch 8a). Title foregrounds the partner
      // business; subtitle carries persona attribution only (avoids
      // redundancy with the title); body opens at the accommodation
      // and references the town as the surrounding area. cornishPhrase
      // is omitted entirely — MemoryPage gates the block on presence.
      const base: MemoryPageContent = {
        title: memoryTitlePartner(partnerBusinessName, profile),
        subtitle: `${formatStayRange(profile.stayStart, profile.stayEnd)} \u00b7 with ${personaName}`,
        paragraphs: [
          buildPartnerMemoryOpener(
            partnerBusinessName,
            profile,
            'Here\u2019s how the week went.',
          ),
          `I helped where I could \u2014 the right beach for the day, what was on at the park, kitchens worth your time, the small things that make a week away in ${townLabel}.`,
          `${partnerBusinessName} hopes you had a good week.`,
        ],
        daysVisited: [],
        placesAcknowledged: [],
        closingLine: 'Come back when you can.',
      };
      return enrichMemoryPage(base, profile, history, partnerDaySummary);
    },
    memoryPageSuggestion:
      'Your week\u2019s set down for you. Tap the book up top to read it back whenever you like.',
    errorReply:
      'Lost the thread there for a moment \u2014 would you mind trying again?',
  };
}
/**
 * Effective-persona resolver \u2014 single entry point for "given a town
 * and a (maybe-null) partner, what persona drives this chat session?"
 *
 * Returns:
 *   - The partner persona (via buildPartnerPersona) when
 *     `partner.persona` is non-null. Server-side guarantee from API
 *     Batch 7b-1: the persona field on PublicPartner is either the
 *     validated public projection or null.
 *   - The town persona otherwise \u2014 no partner attribution, OR
 *     partner.persona null because the jsonb failed Zod validation
 *     server-side (graceful safety net, same posture as API Batch 7a's
 *     buildSystemPrompt fallback).
 *   - null if the town slug is not recognised (caller must handle).
 *
 * Use this instead of calling getPersona / buildPartnerPersona
 * directly \u2014 the branching belongs in one place so MePage, JoinPage,
 * and any future consumer get the same behaviour. Mirrors
 * `buildSystemPrompt`'s branching on the API (Batch 7a) so client- and
 * server-side identity choices agree.
 */
export function resolvePersona(
  townSlug: string,
  partner: PublicPartner | null,
): Persona | null {
  if (partner?.persona) {
    return buildPartnerPersona(partner, townSlug);
  }
  return getPersona(townSlug);
}
